import { useState } from 'react'
import type { CreateCommandRequest, RecordedFile } from '@/types'

interface Props {
  files: RecordedFile[]
  sourceKind?: 'recording' | 'inference_recording'
  initialSelectedFileIds?: string[]
  canDeleteLocal?: boolean
  onSubmit: (req: CreateCommandRequest) => void
  onCancel: () => void
  submitting: boolean
}

function uploadTargetParts(file: RecordedFile): { prefix: string; name: string } {
  const name = file.file_name ?? file.id
  const targetName = file.upload_target_file_name
  if (!targetName || !file.file_name || targetName === file.file_name) {
    return { prefix: '', name }
  }
  if (targetName.endsWith(file.file_name)) {
    return {
      prefix: targetName.slice(0, targetName.length - file.file_name.length),
      name: file.file_name,
    }
  }
  return { prefix: '', name: targetName }
}

function UploadTargetName({ file }: { file: RecordedFile }) {
  const parts = uploadTargetParts(file)
  const title = file.upload_target_file_name
    ? `Upload target: ${file.upload_target_file_name}`
    : file.file_name ?? file.id

  return (
    <span title={title} style={targetNameStyle}>
      {parts.prefix && <span style={targetPrefixStyle}>{parts.prefix}</span>}
      <span>{parts.name}</span>
    </span>
  )
}

export default function UploadStartForm({
  files,
  sourceKind = 'recording',
  initialSelectedFileIds = [],
  canDeleteLocal = false,
  onSubmit,
  onCancel,
  submitting,
}: Props) {
  const [mode, setMode] = useState<'files' | 'date_range'>('files')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [bandwidthLimit, setBandwidthLimit] = useState('')
  const [cleanAfterUpload, setCleanAfterUpload] = useState(
    canDeleteLocal && sourceKind === 'inference_recording',
  )
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const uploadableFiles = files.filter(
    (f) => f.upload_status === 'pending' || f.upload_status === 'failed',
  )
  const uploadableFileIds = new Set(uploadableFiles.map((file) => file.id))
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(initialSelectedFileIds.filter((id) => uploadableFileIds.has(id))),
  )

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function selectAll() {
    if (selectedIds.size === uploadableFiles.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(uploadableFiles.map((f) => f.id)))
    }
  }

  function validate(): boolean {
    const e: Record<string, string> = {}
    if (mode === 'files' && selectedIds.size === 0) {
      e.files = 'Select at least one file'
    }
    if (mode === 'date_range') {
      if (sourceKind === 'inference_recording') {
        e.files = 'Inference recording uploads require selected files'
      }
      if (!startDate) e.startDate = 'Required'
      if (!endDate) e.endDate = 'Required'
      if (startDate && endDate && startDate > endDate) {
        e.endDate = 'End date must be after start date'
      }
    }
    if (bandwidthLimit && (isNaN(Number(bandwidthLimit)) || Number(bandwidthLimit) <= 0)) {
      e.bandwidth = 'Must be a positive number in Mbps'
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    const params: Record<string, unknown> = { mode, source_kind: sourceKind }
    if (mode === 'files') {
      params.file_ids = Array.from(selectedIds)
    } else {
      params.start_date = startDate
      params.end_date = endDate
    }
    if (bandwidthLimit) {
      params.bandwidth_limit_mbps = Number(bandwidthLimit)
    }
    if (cleanAfterUpload) {
      params.clean_after_upload = true
      const fileCount = mode === 'files' ? selectedIds.size : 0
      const msg = mode === 'files'
        ? `업로드 성공한 ${fileCount}개 파일을 노드에서 삭제합니다. 되돌릴 수 없습니다. 진행할까요?`
        : '업로드 성공한 파일들을 노드에서 삭제합니다. 되돌릴 수 없습니다. 진행할까요?'
      if (!window.confirm(msg)) return
    }
    const trimmedReason = reason.trim()
    onSubmit({
      type: 'upload.start',
      params,
      reason: trimmedReason || undefined,
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={fieldStyle}>
        <label style={labelStyle}>Target selection</label>
        <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="radio"
              checked={mode === 'files'}
              onChange={() => setMode('files')}
            />
            Select files
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="radio"
              checked={mode === 'date_range'}
              disabled={sourceKind === 'inference_recording'}
              onChange={() => setMode('date_range')}
            />
            Date range
          </label>
        </div>
      </div>

      {mode === 'files' && (
        <div style={fieldStyle}>
          {uploadableFiles.length === 0 ? (
            <p style={{ fontSize: 13, color: '#aaa', margin: 0 }}>No pending/failed files available for upload</p>
          ) : (
            <div
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: 4,
                maxHeight: 180,
                overflowY: 'auto',
              }}
            >
              <div
                style={{
                  padding: '6px 10px',
                  background: '#f8f8f8',
                  borderBottom: '1px solid #e0e0e0',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.size === uploadableFiles.length && uploadableFiles.length > 0}
                  onChange={selectAll}
                />
                <span style={{ color: '#888' }}>
                  {selectedIds.size}/{uploadableFiles.length} selected
                </span>
              </div>
              {uploadableFiles.map((f) => (
                <label
                  key={f.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 10px',
                    fontSize: 13,
                    cursor: 'pointer',
                    borderBottom: '1px solid #f5f5f5',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(f.id)}
                    onChange={() => toggleSelect(f.id)}
                  />
                  <UploadTargetName file={f} />
                  <span style={{ fontSize: 11, color: '#aaa' }}>
                    {f.upload_status}
                  </span>
                </label>
              ))}
            </div>
          )}
          {errors.files && <div style={errStyle}>{errors.files}</div>}
        </div>
      )}

      {mode === 'date_range' && (
        <div style={fieldStyle}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Start date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={inputStyle}
              />
              {errors.startDate && <div style={errStyle}>{errors.startDate}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>End date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={inputStyle}
              />
              {errors.endDate && <div style={errStyle}>{errors.endDate}</div>}
            </div>
          </div>
        </div>
      )}

      <div style={fieldStyle}>
        <label style={labelStyle}>Bandwidth limit (Mbps, optional)</label>
        <input
          type="number"
          min={1}
          placeholder="e.g. 20"
          value={bandwidthLimit}
          onChange={(e) => setBandwidthLimit(e.target.value)}
          style={{ ...inputStyle, width: 120 }}
        />
        {errors.bandwidth && <div style={errStyle}>{errors.bandwidth}</div>}
      </div>

      {canDeleteLocal && (
        <div style={fieldStyle}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={cleanAfterUpload}
              onChange={(e) => setCleanAfterUpload(e.target.checked)}
            />
            <span>
              업로드 성공 후 노드에서 파일 삭제
              <span style={{ marginLeft: 6, fontSize: 11, color: '#c62828' }}>(되돌릴 수 없음)</span>
            </span>
          </label>
        </div>
      )}

      <div style={fieldStyle}>
        <label htmlFor="upload-reason" style={labelStyle}>Reason (optional)</label>
        <textarea
          id="upload-reason"
          rows={2}
          placeholder="e.g. weekly NAS sync for site-a"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" onClick={onCancel} style={cancelBtnStyle} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" style={submitBtnStyle} disabled={submitting}>
          {submitting ? 'Sending...' : 'Start upload'}
        </button>
      </div>
    </form>
  )
}

const fieldStyle: React.CSSProperties = { marginBottom: 14 }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }
const errStyle: React.CSSProperties = { fontSize: 11, color: '#c62828', marginTop: 3 }
const targetNameStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 12,
  flex: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
  lineHeight: 1.35,
}
const targetPrefixStyle: React.CSSProperties = {
  color: '#9aa0a6',
}
const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid #d0d0d0',
  borderRadius: 4,
  fontSize: 13,
  fontFamily: 'inherit',
  background: '#fff',
  width: '100%',
  boxSizing: 'border-box',
}
const cancelBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}
const submitBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#3949ab',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}
